import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, effect, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { ProductLinkCardComponent } from 'src/app/catalog/components/product-link-card/product-link-card.component';
import { ImageMapItem } from 'src/app/product-galleries/models/product-gallery.model';

@Component({
  selector: 'app-media-point-map',
  imports: [ProductLinkCardComponent, OverlayModule],
  templateUrl: './media-point-map.component.html',
  styleUrl: './media-point-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaPointMapComponent {
  points = input<ImageMapItem[] | null>();
  connectedElement = input<HTMLElement | null>();
  hostIsConnnected = input<boolean>(false);
  showMinimalDotsFirst = input<boolean>(true);

  elementEnter = output<void>();

  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 12,
    },
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -12,
    },
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 12,
    },
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -12,
    },
  ];

  lastHoveredPointId: number | null = null;
  cd = inject(ChangeDetectorRef);
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private router = inject(Router);
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.closeOverlay(), 150);
  }

  cancelClose() {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  constructor() {
    effect(onCleanup => {
      const targets = [this.connectedElement(), this.hostIsConnnected() ? this.hostElement.nativeElement : null].filter(
        (target): target is HTMLElement => !!target
      );

      if (!targets.length) return;

      for (const target of targets) target.classList.add('media-point-map-container');

      const onMouseEnter = () => {
        this.cancelClose();
        this.elementEnter.emit();
      };

      const onMouseLeave = () => this.scheduleClose();

      const onClick = (event: MouseEvent) => {
        if (this.isOverlayTarget(event.target)) return;
        this.closeOverlay();
      };

      for (const target of targets) {
        target.addEventListener('mouseenter', onMouseEnter);
        target.addEventListener('mouseleave', onMouseLeave as EventListener);
        target.addEventListener('click', onClick as EventListener);
      }

      onCleanup(() => {
        for (const target of targets) {
          target.classList.remove('media-point-map-container');
          target.removeEventListener('mouseenter', onMouseEnter);
          target.removeEventListener('mouseleave', onMouseLeave as EventListener);
          target.removeEventListener('click', onClick as EventListener);
        }
      });
    });
  }

  private isOverlayTarget(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('.media-point-map-overlay');
  }

  openOverlay(event: Event, point: ImageMapItem, isClick = false) {
    event.stopPropagation();
    this.lastHoveredPointId = point.id;
    this.cd.markForCheck();

    //  if is non-touch device, go to link on click, otherwise open overlay
    if (isClick && !('ontouchstart' in window)) {
      this.router.navigate(['/', 'products', point.handle]);
    }
  }

  closeOverlay() {
    this.lastHoveredPointId = null;
    this.cd.markForCheck();
  }
}
